import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import SEO from '@/components/shared/SEO';
import { BreadcrumbSchema } from '@/components/shared/JsonLdSchemas';
import JsonLd from '@/components/shared/JsonLd';
import { getArticleBySlug } from '@/data/articles';
import {
  ArticleHero,
  ArticleTakeaways,
  ArticleTableOfContents,
  ArticleSection,
  ArticleCTA,
  ArticleProductRecommendations
} from '@/components/articles';
import { ArrowLeft } from 'lucide-react';

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <Layout>
        <div className="container-portico py-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-stone-800 mb-4">Article Not Found</h1>
          <p className="text-stone-500 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/resources" className="text-primary font-medium hover:underline">
            Back to Resources
          </Link>
        </div>
      </Layout>
    );
  }

  // Determine where to place mid-article CTAs (roughly 1/3 and 2/3 through)
  const totalSections = article.sections.length;
  const ctaAfterIndex1 = Math.floor(totalSections / 3);
  const ctaAfterIndex2 = Math.floor((totalSections * 2) / 3);

  return (
    <Layout>
      <SEO
        title={article.title}
        description={article.seoDescription || article.intro}
        canonicalUrl={`/resources/${article.slug}`}
        ogImage={article.heroImage}
        ogType="article"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Resources", url: "/resources" },
        { name: article.title }
      ]} />

      {/* Article Schema — JSON-LD injected via textContent (no innerHTML / XSS surface) */}
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.seoDescription || article.intro,
          "image": article.heroImage ? `https://porticosystems.com${article.heroImage}` : undefined,
          "author": { "@type": "Organization", "name": article.author || "Portico Systems" },
          "publisher": { "@type": "Organization", "name": "Portico Systems", "logo": { "@type": "ImageObject", "url": "https://porticosystems.com/Assets/logos/portico-logo.svg" } },
          "datePublished": article.date,
          "mainEntityOfPage": `https://porticosystems.com/resources/${article.slug}`
        }}
      />

      {/* Hero */}
      <ArticleHero
        eyebrow={article.eyebrow}
        title={article.title}
        intro={article.intro}
        author={article.author}
        date={article.date}
        readingTime={article.readingTime}
        heroImage={article.heroImage}
        heroImageAlt={article.heroImageAlt}
      />

      {/* Takeaways */}
      <ArticleTakeaways takeaways={article.takeaways} />

      {/* Main content area with TOC sidebar */}
      <div className="container-portico py-12 md:py-16">
        {/* Back link */}
        <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-primary mb-10 transition-colors" data-testid="article-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Resources
        </Link>

        <div className="flex gap-12 lg:gap-16">
          {/* TOC - left sidebar on desktop */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <ArticleTableOfContents sections={article.sections.filter(s => s.id)} />
          </div>

          {/* Article body */}
          <article className="flex-1 min-w-0 max-w-3xl">
            <div className="space-y-14">
              {article.sections.map((section, i) => (
                <React.Fragment key={section.id || i}>
                  <ArticleSection section={section} index={i} />

                  {/* Mid-article CTA after 1/3 */}
                  {i === ctaAfterIndex1 && article.ctaBlock && (
                    <ArticleCTA
                      title="Need Expert Guidance?"
                      text="Our flooring specialists can help you identify the right solutions for your specific environment."
                      buttonText="Schedule a Consultation"
                      buttonLink="/contact"
                      variant="inline"
                    />
                  )}

                  {/* Mid-article CTA after 2/3 */}
                  {i === ctaAfterIndex2 && article.ctaBlock && (
                    <ArticleCTA
                      title="View Flooring Solutions"
                      text="Explore our complete range of commercial flooring products designed for safety and performance."
                      buttonText="Browse Products"
                      buttonLink="/products"
                      variant="inline"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Final CTA */}
            {article.ctaBlock && (
              <ArticleCTA
                title={article.ctaBlock.title}
                text={article.ctaBlock.text}
                buttonText={article.ctaBlock.buttonText}
                buttonLink={article.ctaBlock.buttonLink}
              />
            )}
          </article>
        </div>
      </div>

      {/* Mobile TOC */}
      <ArticleTableOfContents sections={article.sections.filter(s => s.id)} mobileOnly />

      {/* Product Recommendations */}
      <ArticleProductRecommendations
        recommendations={article.productRecommendations}
        introText="Based on the topics covered in this article, these product categories may be relevant to your needs."
      />
    </Layout>
  );
};

export default ArticlePage;
